import React from 'react';
import { Plus } from 'lucide-react';
import { AnalyzingMeter, Button, ClipRow, instrumentLabel } from '../ds';
import { cx, focusRing, motion } from '../ds/classes';
import { useRunningAnalyses } from '../../hooks/analysisStore';

const Group = ({ title, clips, selectedId, analyzing, onSelect }) => (
  <div className="flex flex-col gap-1.5">
    <span className="px-space-1 text-label text-muted">{title}</span>
    {clips.map((clip) => (
      <ClipRow
        key={clip.id}
        name={clip.name}
        tag={instrumentLabel(clip.category, clip.instrument_type)}
        score={clip.score}
        version={clip.latest_version}
        analyzing={analyzing.includes(clip.id)}
        selected={clip.id === selectedId}
        onSelect={() => onSelect(clip.id)}
      />
    ))}
  </div>
);

/** One clip in the phone strip: its name and score (or the analyzing meter), raised when it's the open one */
const ClipPill = ({ clip, selected, analyzing, onSelect }) => (
  <button
    type="button"
    aria-current={selected ? 'true' : undefined}
    onClick={onSelect}
    className={cx(
      'flex-none min-h-target-min px-space-4 inline-flex items-center gap-space-2 rounded-radius-pill font-text text-small font-bold text-ink whitespace-nowrap transition-colors',
      selected ? 'bg-raised' : 'bg-surface hover:bg-raised',
      motion,
      focusRing
    )}
  >
    {clip.name}
    {analyzing ? (
      <>
        <AnalyzingMeter />
        <span className="sr-only">, analyzing</span>
      </>
    ) : clip.score != null ? (
      <span className={cx('font-mono text-data-sm', selected ? 'text-ink' : 'text-muted')}>{clip.score}</span>
    ) : (
      <span className="sr-only">, not scored</span>
    )}
  </button>
);

/**
 * The project's clips: stems first, then the full mix. Beside the chat from tablet up (narrower
 * until laptop width); on phones it folds into a strip of clips that scrolls sideways above the chat.
 */
export const ClipSidebar = ({ clips, selectedId, onSelect, onAddClip }) => {
  const stems = clips.filter((clip) => clip.kind !== 'mix');
  const mixes = clips.filter((clip) => clip.kind === 'mix');
  const analyzing = useRunningAnalyses();

  return (
    <>
      <nav aria-label="Clips in this project" className="hidden md:flex w-[240px] lg:w-sidebar-min flex-none flex-col gap-space-4 p-space-4 lg:p-space-5 bg-chrome overflow-y-auto font-text">
        <div className="flex items-center justify-between gap-space-2 px-space-1">
          <h2 className="m-0 font-display text-project text-ink">Clips</h2>
          <Button variant="outline" icon={Plus} onClick={onAddClip}>Add clip</Button>
        </div>
        {clips.length === 0 && (
          <p className="m-0 px-space-1 text-small text-ink-soft">No clips yet. Add a stem or your full mix to get a score.</p>
        )}
        {stems.length > 0 && <Group title="Stems" clips={stems} selectedId={selectedId} analyzing={analyzing} onSelect={onSelect} />}
        {mixes.length > 0 && <Group title="Full mix" clips={mixes} selectedId={selectedId} analyzing={analyzing} onSelect={onSelect} />}
        <p className="m-0 px-space-1 text-caption text-muted">
          Stems get a stem health score. A full mix gets a mastering readiness score.
        </p>
      </nav>

      <nav aria-label="Clips in this project" className="md:hidden flex-none flex gap-space-2 px-space-3 py-space-2 overflow-x-auto bg-chrome border-b border-raised">
        {[...stems, ...mixes].map((clip) => (
          <ClipPill key={clip.id} clip={clip} selected={clip.id === selectedId} analyzing={analyzing.includes(clip.id)} onSelect={() => onSelect(clip.id)} />
        ))}
        <button
          type="button"
          onClick={onAddClip}
          className={cx(
            'flex-none min-h-target-min px-space-4 inline-flex items-center gap-1.5 rounded-radius-pill border border-amber bg-transparent font-text text-small font-bold text-amber whitespace-nowrap transition-colors hover:bg-raised',
            motion,
            focusRing
          )}
        >
          <Plus size={16} strokeWidth={2.5} aria-hidden="true" />
          Add clip
        </button>
      </nav>
    </>
  );
};
